
import { defineComponent, ref, ComputedRef, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { QSelect } from 'quasar';
import { useStore } from 'src/store';
import { Boards } from 'src/api';
import { useApiClient } from 'src/services/connection';

const qSelectRef = ref<QSelect>();

type Option = Boards.Types.Board;

const filterOptions = (options: Option[], needle: string): Option[] => {
  if (!needle) {
    return options;
  }

  return options.filter((option) => {
    return (
      `${option.id}`.includes(needle) ||
      option.name.toLowerCase().includes(needle)
    );
  });
};

export default defineComponent({
  name: 'BoardLookup',
  props: {
    label: {
      type: String,
      required: true,
    },
  },
  setup() {
    const $router = useRouter();
    const $store = useStore();
    const selected = ref(null);
    const options = ref<Option[]>([]);
    const error = ref('');
    const hasError = computed(() => !!error.value);
    const loading = computed(() => $store.state.loading.boards);
    const boards = computed(() => $store.state.distribution.boards);
    const filterValue = ref('');

    const loadData = async () => {
      await $store.dispatch('distribution/setBoards', []);

      return useApiClient(async (client) => {
        const batchCb = (boards: Boards.Types.Board[]) => {
          options.value = [...options.value, ...boards];
          qSelectRef.value?.refresh();
          qSelectRef.value?.filter(filterValue.value);
        };

        await client.boards.loadAllBoards({
          batchCb,
        });

        return $store.dispatch('loading/setBoardsLoading', false);
      });
    };

    onMounted(loadData);

    function filterOptionsOnInput(items: ComputedRef<Option[]>) {
      return (filter: string, update: (callback: () => void) => void) => {
        filterValue.value = filter;
        update(() => {
          options.value = filterOptions(items.value, filter.toLowerCase());
        });
      };
    }

    return {
      options,
      selected,
      qSelectRef,
      showPopupFn: () => {
        qSelectRef.value?.showPopup();
      },
      popupHideFn: () => {
        selected.value = null;
      },
      inputFn: (opt: Option) => {
        void $router.push({
          name: 'board',
          params: { boardId: `${opt.id}` },
        });
      },
      filterFn: filterOptionsOnInput(boards),
      loading,
      error,
      hasError,
    };
  },
});
